import { groupBy, nonNullable } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import offerPageURL from 'lib/offer/offerPageURL'
import { getConfidenceLabels, getLabels } from 'checkout/lib/utils/accommodation/label'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { excludeNullOrUndefined, getAccommodationDesignationWithCruise } from 'checkout/utils'

import { getFullOffers } from 'selectors/offerSelectors'
import { isLEHotel } from 'lib/offer/offerTypes'
import getFlightBundledItemIds from '../getFlightBundledItemIds'
import { findPostPurchaseCheckout, isBNBLLEHotelItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { getLeHotelItems } from '../hotels'
import { getCheckoutLuxPlusTier } from '../../checkoutLuxPlusSelectors'
import { getDepositServiceFeeConfig, getDefaultDepositAmountPercentage } from '../../featureConfig/deposit'
import { checkoutWithMemberPrice } from '../luxPlusSubscription'
import { getLEPackageItemView } from 'checkout/lib/utils/accommodation/view'
import { getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import getLoyaltyStayUpgradeBenefit from 'loyalty/selectors/getLoyaltyStayUpgradeBenefit'
import getLoyaltyBenefitAvailability from 'loyalty/lib/determineLoyaltyBenefitAvailability'
import getCurrentAdjustedPaymentSchedule from 'checkout/selectors/payment/paymentSchedule/getCurrentAdjustedPaymentSchedule'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'

function pkgMatch(item: App.Checkout.LEAccommodationItem) {
  return (pkg: App.Package): boolean => {
    return pkg.id === item.packageId && pkg.duration === item.duration
  }
}

const getHotelItemViews = createSelector(
  (state: App.State) => getLeHotelItems(state),
  (state: App.State) => getDepositServiceFeeConfig(state),
  (state: App.State) => getDefaultDepositAmountPercentage(state),
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.calendar.calendarsByOccupancy,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (state: App.State) => state.offer.propertyRates,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getCheckoutLuxPlusTier(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => getFlightBundledItemIds(state),
  (state: App.State) => getLoyaltyStayUpgradeBenefit(state),
  (state: App.State) => getCurrentAdjustedPaymentSchedule(state),
  (state: App.State) => getCruiseItems(state),
  (
    cartItems,
    serviceFee,
    defaultDepositAmountPercentage,
    offers,
    calendarsByOccupancy,
    availableRatesByOccupancy,
    propertyRatesByKey,
    postPurchase,
    existingOrder,
    luxPlusTier,
    checkoutWithMemberPrice,
    bundledItemIds,
    loyaltyRoomUpgradeBenefit,
    adjustedPaymentSchedule,
    cruiseItems,
  ): App.WithDataStatus<Array<App.Checkout.LEAccommodationOfferView>> => {
    // Each item = 1 room, but we want to process the same dates/offer as a single view
    const itemsByBookingKey = groupBy(cartItems, item => {
      if (isBNBLLEHotelItem(item)) {
        return `${item.offerId}-bnbl-${item.duration}`
      }
      return `${item.offerId}-${item.checkIn}-${item.checkOut}`
    })
    const itemGroups = itemsByBookingKey.values().map(items => ({
      offerId: items[0].offerId,
      items,
    })).toArray()

    const views = itemGroups.map((itemGroup): App.WithDataStatus<App.Checkout.LEAccommodationOfferView | undefined> => {
      const { offerId, items } = itemGroup

      const offer = offers[offerId]

      if (!isLEHotel(offer)) {
        return {
          hasRequiredData: false,
          data: undefined,
        }
      }

      const firstItem = items[0]

      // The items could be different packages, but since they're grouped by duration/dates,
      // we know the items in each have the same duration. So we can look up the duration label
      // of the first item.
      const durationLabel = offer.packages.find(pkgMatch(firstItem))?.durationLabel ?? getCurrentIntl().formatMessage({ id: 'checkout.hotel.nightCount', defaultMessage: '{count, plural, one {# night} other {# nights}}', description: 'Night count fallback for hotel duration label in the checkout flow' }, { count: firstItem.duration })

      const itemViewsWithStatuses = items.map(item => getLEPackageItemView(
        getCurrentIntl(),
        item,
        calendarsByOccupancy,
        availableRatesByOccupancy,
        propertyRatesByKey,
        checkoutWithMemberPrice,
        offer,
        postPurchase,
        existingOrder,
        luxPlusTier,
        loyaltyRoomUpgradeBenefit && !!getLoyaltyBenefitAvailability(loyaltyRoomUpgradeBenefit),
        adjustedPaymentSchedule,
      ))

      const checkIn = isInstantBookingLEHotelItem(firstItem) ? firstItem.checkIn : undefined
      const checkOut = isInstantBookingLEHotelItem(firstItem) ? firstItem.checkOut : undefined

      const view: App.Checkout.LEAccommodationOfferView = {
        offerId,
        offer,
        offerPageUrl: offerPageURL(offer),
        ...getLabels(offer),
        bedGroups: [],
        confidenceLabels: getConfidenceLabels(getCurrentIntl(), offer),
        duration: firstItem.duration,
        startDate: checkIn,
        endDate: checkOut,
        image: offer.image,
        location: getLocationString(getCurrentIntl(), offer),
        locationCountry: offer.property.geoData.country ?? undefined,
        locationCountryCode: offer.property.geoData.countryCode ?? undefined,
        locationState: offer.property.geoData.administrativeAreaLevel1 ?? undefined,
        occupancy: itemGroup.items.map(item => item.occupancy).filter(excludeNullOrUndefined),
        offerType: offer.type,
        reservationType: firstItem.reservationType ?? 'instant_booking',
        urgencyLabels: offer.urgencyTags,
        saleUnit: offer.saleUnitLong,
        durationLabel: durationLabel.toLocaleLowerCase(),
        offerLoaded: true,
        itemViews: itemViewsWithStatuses.map(i => i.data),
        propertyTimezone: offer.property.timezone ?? '',
        bundledWithFlightsOnly: offer.bundledWithFlightsOnly,
        bundledWithTour: items.some(item => !!item.bundledItemIds?.length),
        designation: getAccommodationDesignationWithCruise(checkIn, checkOut, cruiseItems),
        depositType: 'percentage',
        depositAmount: getDepositAmountPercentage(offer, defaultDepositAmountPercentage),
        serviceFee,
        isBundled: items.some(item => bundledItemIds.has(item.itemId)),
      }

      return {
        data: view,
        hasRequiredData: itemViewsWithStatuses.every(itemView => itemView.hasRequiredData),
      }
    })

    return {
      data: nonNullable(views.map(view => view.data)),
      hasRequiredData: views.every(view => view.hasRequiredData),
    }
  },
)

export default getHotelItemViews
